import { ITimer } from "../../../services/TimerService";

export type TimerTarget =
  | {
      type: "work";
      workId: string;
    }
  | {
      type: "workTask";
      workId: string;
      jobTaskId: string;
    };

export const RUNNING_TIMER_COLOR = "#6E3BE9";
export const DEFAULT_WORK_TASK_BUDGET_SECONDS = 5 * 60 * 60;

export const isTimerActive = (timer: ITimer | null): boolean =>
  timer?.timerStatus === "started" || timer?.timerStatus === "paused";

export const isTimerRunning = (timer: ITimer | null): boolean =>
  timer?.timerStatus === "started";

export const isTimerPaused = (timer: ITimer | null): boolean =>
  timer?.timerStatus === "paused";

export const getTimerTargetType = (timer: ITimer | null): TimerTarget["type"] =>
  timer?.targetType === "workTask" || timer?.jobTaskId ? "workTask" : "work";

export const isTimerForTarget = (
  timer: ITimer | null,
  target: TimerTarget,
): boolean => {
  if (!timer || timer.workId !== target.workId) {
    return false;
  }

  if (target.type === "workTask") {
    return timer.jobTaskId === target.jobTaskId;
  }

  return getTimerTargetType(timer) === "work";
};

export const canStartTimerForTarget = (
  timer: ITimer | null,
  target: TimerTarget,
): boolean => !isTimerActive(timer) || isTimerForTarget(timer, target);

export const formatTimerHoursMinutes = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(seconds, 0) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);

  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

export const formatTimerHoursMinutesSeconds = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds)
    ? Math.max(Math.floor(seconds), 0)
    : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const getWorkTaskBudgetSeconds = (timeBudget?: number): number => {
  const budgetHours = Number(timeBudget);

  if (!Number.isFinite(budgetHours)) {
    return DEFAULT_WORK_TASK_BUDGET_SECONDS;
  }

  return Math.max(Math.round(budgetHours * 60 * 60), 0);
};

export const getWorkTaskTrackedSeconds = (duration?: number | null): number => {
  const durationSeconds = Number(duration);

  if (!Number.isFinite(durationSeconds)) {
    return 0;
  }

  return Math.max(Math.round(durationSeconds), 0);
};

/**
 * Work item Time tab button (QA A5, 2026-10-22): Pause/Continue only when the
 * running timer is this work item's own timer; otherwise Start. While another
 * timer (other work item or a subtask) is running, Start can't be used yet
 * ("blocked"), like the subtask timers.
 */
export const workTimeTabButton = (
  timer: ITimer | null,
  workId: string,
): "start" | "blocked" | "pause" | "continue" => {
  const target: TimerTarget = { type: "work", workId };
  if (isTimerForTarget(timer, target) && isTimerActive(timer)) {
    return isTimerPaused(timer) ? "continue" : "pause";
  }
  return isTimerActive(timer) ? "blocked" : "start";
};
